import { MoreVertical, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { FUNNEL_STAGE_LABELS, FUNNEL_STAGES, type DialogDto } from '../../api/types';
import { useDialogHandoffMutation, useDialogStageMutation, useDialogTagsMutation } from '../../hooks/use-dialogs';
import { cn } from '../../lib/cn';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

export interface DialogRowMenuProps {
  dialog: DialogDto;
  /** 'row' renders as a "…" icon button (inbox list row); 'inline' renders full-size buttons (dialog header). */
  variant?: 'row' | 'inline';
  /** Controlled open state for 'row' variant, so a right-click on the row can open the same menu. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Status management (§U1 item 5): funnel stage, handoff and tags, from either the list row's
 * "…" menu (or a right-click on the row) or the dialog header. Every action is optimistic
 * (see hooks/use-dialogs.ts) with rollback + toast on failure.
 */
export function DialogRowMenu({ dialog, variant = 'row', open: openProp, onOpenChange }: DialogRowMenuProps) {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = useCallback(
    (next: boolean | ((v: boolean) => boolean)) => {
      setOpenState((prev) => {
        const value = typeof next === 'function' ? next(prev) : next;
        onOpenChange?.(value);
        return value;
      });
    },
    [onOpenChange],
  );
  const [tagInput, setTagInput] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const stageMutation = useDialogStageMutation();
  const handoffMutation = useDialogHandoffMutation();
  const tagsMutation = useDialogTagsMutation();

  useEffect(() => {
    if (!open) return;
    function onClickAway(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, [open, setOpen]);

  function addTag(e: React.FormEvent) {
    e.preventDefault();
    const tag = tagInput.trim();
    if (!tag || dialog.contact.tags.includes(tag)) return;
    tagsMutation.mutate({ dialog, tags: [...dialog.contact.tags, tag] });
    setTagInput('');
  }

  function removeTag(tag: string) {
    tagsMutation.mutate({ dialog, tags: dialog.contact.tags.filter((t) => t !== tag) });
  }

  const panel = (
    <div
      ref={variant === 'row' ? ref : undefined}
      onClick={(e) => e.stopPropagation()}
      className="flex w-64 flex-col gap-3 p-3 text-sm"
    >
      <div>
        <div className="mb-1 text-xs font-medium text-muted-fg">Стадия воронки</div>
        <select
          aria-label="Стадия воронки"
          className="h-8 w-full rounded border border-border bg-card px-2 text-sm"
          value={dialog.contact.funnelStage}
          disabled={stageMutation.isPending}
          onChange={(e) => stageMutation.mutate({ dialog, stage: e.target.value })}
        >
          {FUNNEL_STAGES.map((s) => (
            <option key={s} value={s}>
              {FUNNEL_STAGE_LABELS[s]}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-wrap gap-2">
        {dialog.state === 'AGENT_ACTIVE' && (
          <Button
            size="sm"
            disabled={handoffMutation.isPending}
            onClick={() => handoffMutation.mutate({ dialog, to: 'human' })}
          >
            Взять в работу
          </Button>
        )}
        {dialog.state === 'HUMAN_ACTIVE' && (
          <>
            <Button
              size="sm"
              variant="secondary"
              disabled={handoffMutation.isPending}
              onClick={() => handoffMutation.mutate({ dialog, to: 'agent', mode: 'continue' })}
            >
              Вернуть агенту
            </Button>
          </>
        )}
      </div>

      <div>
        <div className="mb-1 text-xs font-medium text-muted-fg">Теги</div>
        <div className="mb-2 flex flex-wrap gap-1">
          {dialog.contact.tags.length === 0 && <span className="text-xs text-muted-fg">Нет тегов</span>}
          {dialog.contact.tags.map((tag) => (
            <Badge key={tag} className="gap-1">
              {tag}
              <button type="button" aria-label={`Убрать тег ${tag}`} onClick={() => removeTag(tag)}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
        <form onSubmit={addTag} className="flex gap-1">
          <input
            aria-label="Новый тег"
            className="h-8 flex-1 rounded border border-border bg-card px-2 text-sm"
            placeholder="Добавить тег…"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
          />
          <Button type="submit" size="sm" variant="secondary" disabled={!tagInput.trim()}>
            +
          </Button>
        </form>
      </div>
    </div>
  );

  if (variant === 'inline') return panel;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="Управление диалогом"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={cn('rounded p-1 text-muted-fg hover:bg-muted hover:text-fg', open && 'bg-muted text-fg')}
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-20 rounded border border-border bg-card shadow-lg">{panel}</div>
      )}
    </div>
  );
}
